import Button from '@mui/material/Button'
import { PPDialog } from 'components/PPDialog'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { of, Subject } from 'rxjs'
import { catchError, mergeMap } from 'rxjs/operators'
import { checkTokens } from 'services/auth'
import { downgradeFree, getAccountInfo } from 'services/settings'
import { message } from 'services/snackbar'
import { isTrialExpiredSelector } from 'services/users/selectors'
import { StoreThunkDispatch } from 'store/state'
import styles from './TrialPopups.pcss'

// Blocking trial gate — must sit above other modals and snackbars.
const DIALOG_Z_INDEX = 1400

export function TrialExpiredPopup() {
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [downgrading, setDowngrading] = useState(false)
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const trialExpired = useSelector(isTrialExpiredSelector)

  const checkStatus$ = useRef<Subject<any>>(undefined)

  const isSubscriptionsPage = location.pathname === '/settings/subscription'
  const isOnboardingPage = location.pathname.startsWith('/onboarding')

  useEffect(() => {
    checkStatus$.current = new Subject()
    checkStatus$.current.pipe(
      mergeMap(() => {
        return dispatch(checkTokens())
          .pipe(catchError((error) => of(error)))
      }))
      .subscribe(() => { setLoading(false) })

    if (trialExpired) {
      checkStatus$.current.next(null)
    }

    return () => {
      checkStatus$.current?.unsubscribe()
    }

  }, [dispatch, trialExpired])

  const onUpgrade = () => {
    navigate('/settings/subscription')
  }

  const onDowngradeFree = () => {
    if (downgrading) return
    setDowngrading(true)
    dispatch(downgradeFree()).unwrap()
      .then(() => {
        dispatch(checkTokens()).subscribe()
      })
      .then(() => dispatch(getAccountInfo()).unwrap())
      .catch(() => {
        dispatch(message(intl.formatMessage({ id: 'errors.generic-support' }), 'error'))
      })
      .finally(() => {
        setDowngrading(false)
      })
  }

  return (
    <PPDialog
      open={!loading && trialExpired && !isSubscriptionsPage && !isOnboardingPage}
      dismissible={false}
      zIndex={DIALOG_Z_INDEX}
      className={styles.dialog}
      title={<FormattedMessage id="popups.trial-end.title-top" />}
    >
      <div className={`${styles.content} ${styles.trial}`}>
        <h1 className={styles.title}>
          <FormattedMessage id="popups.trial-end.title" />
        </h1>
        <div className={`${styles.actions} ${styles.expired}`}>
          <Button color="primary" variant="contained" size="large" onClick={onUpgrade}>
            See available plans
          </Button>
          <div className={styles['actions-bottom']}>
            <span
              role="button"
              className={`${styles['btn-downgrade']} ${downgrading ? styles['btn-disabled'] : ''}`}
              onClick={onDowngradeFree}
            >
              Use the Free plan
            </span>
          </div>
        </div>
      </div>
    </PPDialog>
  )
}
